<script setup lang="ts">
import {
    ChevronsRight,
    PenLine,
    Send,
    ShieldCheck,
    Users,
    Wand,
} from '@lucide/vue';
import SectionHeading from '@/components/marketing/shared/SectionHeading.vue';

const stages = [
    {
        icon: PenLine,
        title: 'Create',
        description:
            'Your assistant drafts posts, captions and images from what it knows about your business.',
    },
    {
        icon: ShieldCheck,
        title: 'Review',
        description:
            'You see every draft before it goes out, and can ask for changes in plain language.',
    },
    {
        icon: Send,
        title: 'Publish',
        description:
            'Approved content is scheduled and sent to the channels you connect.',
    },
];

const outcomes = [
    {
        icon: Wand,
        title: 'Create with AI',
        description: 'Turn an idea into on-brand content in seconds.',
    },
    {
        icon: Users,
        title: 'Stay in sync',
        description: 'Keep your team, content and calendar aligned.',
    },
    {
        icon: ShieldCheck,
        title: 'Review with confidence',
        description: 'Approve, ask for changes, and publish with ease.',
    },
];
</script>

<template>
    <section id="product" class="scroll-mt-24 py-16 sm:py-20">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
                eyebrow="Product overview"
                title="See your marketing flow in one place."
                description="Create, plan, review and publish from one calm, connected workspace instead of six browser tabs."
            />

            <div
                class="mt-12 flex flex-col items-stretch gap-4 lg:flex-row lg:items-center"
            >
                <template v-for="(stage, index) in stages" :key="stage.title">
                    <div
                        class="bg-card border-border flex-1 rounded-xl border p-6"
                    >
                        <span
                            class="bg-primary/10 text-primary inline-flex size-10 items-center justify-center rounded-lg"
                        >
                            <component
                                :is="stage.icon"
                                class="size-5"
                                aria-hidden="true"
                            />
                        </span>
                        <h3 class="mt-3 text-lg">{{ stage.title }}</h3>
                        <p
                            class="text-muted-foreground mt-1 text-sm leading-relaxed"
                        >
                            {{ stage.description }}
                        </p>
                    </div>
                    <ChevronsRight
                        v-if="index < stages.length - 1"
                        class="text-muted-foreground mx-auto hidden size-5 shrink-0 lg:block"
                        aria-hidden="true"
                    />
                </template>
            </div>

            <div
                class="border-border mt-12 grid gap-8 border-t pt-10 sm:grid-cols-3"
            >
                <div
                    v-for="outcome in outcomes"
                    :key="outcome.title"
                    class="flex gap-3"
                >
                    <span
                        class="bg-secondary/10 text-secondary inline-flex size-10 shrink-0 items-center justify-center rounded-lg"
                    >
                        <component
                            :is="outcome.icon"
                            class="size-5"
                            aria-hidden="true"
                        />
                    </span>
                    <div>
                        <h3 class="text-base">{{ outcome.title }}</h3>
                        <p
                            class="text-muted-foreground mt-1 text-sm leading-relaxed"
                        >
                            {{ outcome.description }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
